// src/components/e-sign/GetSignatures.js
import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  Typography,
  Button,
  Paper,
  TextField,
  Box,
  CircularProgress,
  Chip,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  ButtonGroup
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import PersonIcon from '@material-ui/icons/Person';
import DeleteIcon from '@material-ui/icons/Delete';
import PdfIcon from '@material-ui/icons/PictureAsPdf';
import FolderIcon from '@material-ui/icons/Folder';
import { useHistory, useParams } from 'react-router-dom';
import notify from '../../utils/notifier';
import ESignService from '../../services/ESignService';
import DocuwareTemplateService from '../../services/DocuwareTemplateService';
import VerticalStepperComponent from './VerticalStepperComponent';
import DocumentSelectionModal from './DocumentSelectionModal';
import { Rnd } from 'react-rnd';

// Set up PDF.js worker (add this near the top of your component or in a separate config file)
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 'bold',
  },
  contentArea: {
    display: 'flex',
    flexGrow: 1,
    overflow: 'hidden',
  },
  stepperContainer: {
    width: '200px',
    marginRight: theme.spacing(4),
    paddingRight: theme.spacing(2),
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  contentContainer: {
    flexGrow: 1,
    padding: theme.spacing(2),
    maxWidth: 'calc(100% - 200px)',
    overflow: 'auto',
  },
  uploadArea: {
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(5),
    textAlign: 'center',
    cursor: 'pointer',
    marginTop: theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  fileInput: {
    display: 'none',
  },
  uploadIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  buttonCancel: {
    color: theme.palette.text.secondary,
  },
  buttonNext: {
    backgroundColor: '#051c2c',
    color: 'white',
    '&:hover': {
      backgroundColor: '#083656',
    },
  },
  buttonSend: {
    backgroundColor: '#051c2c',
    color: 'white',
    '&:hover': {
      backgroundColor: '#083656',
    },
  },
  buttonDisabled: {
    backgroundColor: '#CFD8DC',
    color: '#78909C',
  },
  selectionModeContainer: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
  },
  selectionModeButton: {
    textTransform: 'none',
    padding: theme.spacing(1, 3),
    borderRadius: '20px',
    '&.active': {
      backgroundColor: '#051c2c',
      color: 'white',
    },
  },
  uploadedFile: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  fileIcon: {
    marginRight: theme.spacing(2),
    color: '#051c2c',
  },
  fileDetails: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  downloadButton: {
    marginLeft: theme.spacing(2),
  },
  recipientForm: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(3),
    backgroundColor: '#F5F7FA',
    borderRadius: theme.shape.borderRadius,
  },
  recipientChip: {
    margin: theme.spacing(0.5),
    backgroundColor: '#E3F2FD',
  },
  recipientList: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
  },
  messageField: {
    marginTop: theme.spacing(2),
  },
  signatureFieldButtons: {
    display: 'flex',
    overflowX: 'auto',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: '#F0F0F0',
    borderRadius: theme.shape.borderRadius,
    '& > *': {
      marginRight: theme.spacing(1),
    },
  },
  fieldButton: {
    backgroundColor: 'white',
    '&:hover': {
      backgroundColor: '#E0E0E0',
    },
  },
  pdfViewer: {
    width: '100%',
    height: '600px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    backgroundColor: '#F5F5F5',
    position: 'relative',
    overflow: 'auto',
  },
  pdfPage: {
    margin: '0 auto',
    backgroundColor: 'white',
    boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.15)',
    position: 'relative',
  },
  fieldMarker: {
    position: 'absolute',
    border: '2px solid #2196F3',
    backgroundColor: 'rgba(33, 150, 243, 0.1)',
    cursor: 'move',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#2196F3',
    fontWeight: 'bold',
    fontSize: '12px',
    zIndex: 10,
  },
  recipientSelector: {
    position: 'absolute',
    zIndex: 100,
    backgroundColor: 'white',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: theme.spacing(1),
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
  },
  selectedRecipient: {
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    position: 'absolute',
    top: '2px',
    right: '2px',
    fontSize: '12px',
  },
}));

// Define signature field types with display names and colors
const fieldTypes = [
  { type: 'signature', label: 'Signature', color: '#2196F3' },
  // { type: 'name', label: 'Name', color: '#4CAF50' },
  // { type: 'date', label: 'Date', color: '#FF9800' },
  // { type: 'signatureBlock', label: 'Signature Block', color: '#9C27B0' },
  // { type: 'stamp', label: 'Stamp', color: '#F44336' }
];

export default function GetSignatures({ currentCompany }) {
  const classes = useStyles();
  const history = useHistory();
  const { id: editDocumentId } = useParams();
  const fileInputRef = useRef(null);

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [documentId, setDocumentId] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [fileSize, setFileSize] = useState('');
  const [selectionMode, setSelectionMode] = useState('upload'); // 'upload' or 'select'
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedFromSuite, setSelectedFromSuite] = useState(null);

  // Recipients state
  const [recipients, setRecipients] = useState([]);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [newRecipientSigningOrder, setNewRecipientSigningOrder] = useState('');
  const [emailError, setEmailError] = useState('');
  const [signingOrderError, setSigningOrderError] = useState('');
  const [showCC, setShowCC] = useState(false);
  const [message, setMessage] = useState('');

  // CC Recipients state
  const [ccRecipients, setCcRecipients] = useState([]);
  const [newCcRecipientEmail, setNewCcRecipientEmail] = useState('');
  const [ccEmailError, setCcEmailError] = useState('');

  // PDF Viewer state
  const [pdfDimensions, setPdfDimensions] = useState({ width: 595, height: 842 }); // A4 default
  const [scale, setScale] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [pdfNumPages, setPdfNumPages] = useState(null);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfError, setPdfError] = useState(null);

  // Signature fields state
  const [selectedFieldType, setSelectedFieldType] = useState('signature');
  const [signatureFields, setSignatureFields] = useState([]);
  const [isPlacingField, setIsPlacingField] = useState(false);
  const [showRecipientSelector, setShowRecipientSelector] = useState(false);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null);
  const [recipientSelectorPosition, setRecipientSelectorPosition] = useState({ top: 0, left: 0 });
  const [allFieldsAssigned, setAllFieldsAssigned] = useState(false);

  // Color mapping for recipients
  const recipientColors = ['#3f51b5', '#f44336', '#009688', '#ff9800', '#9c27b0', '#607d8b', '#e91e63', '#4caf50'];

  // Stepper steps
  const steps = [
    { label: 'Upload Documents', completed: activeStep > 0 },
    { label: 'Add Recipients', completed: activeStep > 1 },
    { label: 'Add Signature', completed: activeStep > 2 }
  ];

  // Check if editing an existing document
  useEffect(() => {
    if (editDocumentId) {
      fetchDocument(editDocumentId);
    }
  }, [editDocumentId]);

  // Fetch document for editing
  const fetchDocument = async (id) => {
    try {
      setLoading(true);

      //When ready to use the real API:
      const response = await ESignService.getDocument(id);
      if (response && response.data && response.data.success) {
        setDocumentId(response.data.document.id);
        setDocumentTitle(response.data.document.title);
        setUploadedFileName(response.data.document.fileName);
        setFileSize(response.data.document.fileSize);

        // Set recipients
        setRecipients(response.data.recipients || []);

        // Set CC recipients
        setCcRecipients(response.data.ccRecipients || []);

        // Set signature fields
        setSignatureFields(response.data.fields || []);

        // Determine which step to show based on document status
        if (response.data.document.status === 'draft') {
          if (response.data.recipients && response.data.recipients.length > 0) {
            setActiveStep(2); // Go to signature step
          } else {
            setActiveStep(1); // Go to recipients step
          }
        }
      }

    } catch (error) {
      notify({
        type: 'error',
        message: 'An error occurred while loading the document',
      });
      history.push('/e-sign');
    }
  };

  // Check if all fields are assigned to recipients
  useEffect(() => {
    if (signatureFields.length === 0) {
      setAllFieldsAssigned(false);
      return;
    }

    const allAssigned = signatureFields.every(field => field.recipientIndex !== null);
    setAllFieldsAssigned(allAssigned);
  }, [signatureFields]);

  // Handler for file selection
  // Updated handleFileSelect function
  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        notify({
          type: 'error',
          message: 'Only PDF files are supported',
        });
        return;
      }

      setFile(selectedFile);
      // Store the actual file object
      setUploadedFile(selectedFile);

      // Set uploaded file information
      setUploadedFileName(selectedFile.name);
      const title = selectedFile.name.substring(0, selectedFile.name.lastIndexOf('.')) || selectedFile.name;
      setDocumentTitle(title);
      setFileSize(formatFileSize(selectedFile.size));

      // Reset PDF state
      setPdfError(null);
      setPdfPageNumber(1);
    }
  };

  // PDF loading handlers
  const onDocumentLoadSuccess = ({ numPages }) => {
    setPdfNumPages(numPages);
    setPdfError(null);
  };

  const onDocumentLoadError = (error) => {
    setPdfError('Failed to load PDF file');
    notify({
      type: 'error',
      message: 'Failed to load PDF file',
    });
  };


  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handler for upload button click
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  // Handler for selecting document from suite
  const handleSelectFromSuite = () => {
    setShowDocumentModal(true);
  };

  // Handler for document selection from modal
  const handleDocumentSelection = async (document) => {
    try {
      setLoading(true);
      // Fetch the document file from the suite
      const response = await DocuwareTemplateService.viewDocument(document.id);
      
      if (response && response.data) {
        
        // Create a File object from the blob
        const fileName = document.fileName || document.name || 'Selected Document';
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const file = new File([blob], fileName, { type: 'application/pdf' });
        
        // Validate that it's a PDF file
        if (!fileName.toLowerCase().endsWith('.pdf')) {
          throw new Error('Only PDF files are supported for e-signature');
        }
        
        // Set the file as if it was uploaded
        setFile(file);
        setUploadedFile(file);
        setSelectedFromSuite(document);
        setUploadedFileName(fileName);
        setFileSize(formatFileSize(document.fileSize || file.size));
        setDocumentTitle(fileName.substring(0, fileName.lastIndexOf('.')) || fileName);
        
        // Reset document ID since we're treating this as a fresh upload
        setDocumentId('');
        
        // Reset PDF state
        setPdfError(null);
        setPdfPageNumber(1);
        
        notify({
          type: 'success',
          message: `Document "${fileName}" loaded successfully from suite`
        });
        
      } else {
        throw new Error('Failed to fetch document');
      }
    } catch (error) {
      console.error('Error fetching document from suite:', error);
      notify({
        type: 'error',
        message: 'Failed to load document from suite. Please try again.'
      });
    } finally {
      setLoading(false);
      setShowDocumentModal(false);
    }
  };

  // Handler for selection mode change
  const handleSelectionModeChange = (mode) => {
    setSelectionMode(mode);
    
    // Reset state when switching modes
    if (mode === 'upload') {
      setSelectedFromSuite(null);
      setDocumentId('');
    } else {
      setFile(null);
      setUploadedFile(null);
    }
    
    setUploadedFileName('');
    setFileSize('');
    setDocumentTitle('');
    setPdfError(null);
    setPdfPageNumber(1);
  };

  // Validate email format
  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  // Handler for adding a recipient
  const handleAddRecipient = () => {
    // Clear previous errors
    setEmailError('');
    setSigningOrderError('');

    if (!newRecipientEmail.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(newRecipientEmail)) {
      setEmailError('Invalid email format');
      return;
    }

    // Check if email is already added
    if (recipients.some(r => r.email.toLowerCase() === newRecipientEmail.toLowerCase())) {
      setEmailError('This email is already added');
      return;
    }

    // Parse and validate signing order
    let signingOrder = 1; // Default to 1
    if (newRecipientSigningOrder.trim()) {
      const parsedOrder = parseInt(newRecipientSigningOrder.trim(), 10);
      const maxAllowedOrder = recipients.length + 1;
      
      if (isNaN(parsedOrder) || parsedOrder < 1) {
        setSigningOrderError('Signing order must be a positive number');
        return;
      }
      
      if (parsedOrder > maxAllowedOrder) {
        setSigningOrderError(`Signing order cannot be greater than ${maxAllowedOrder} (current recipients + 1)`);
        return;
      }
      
      signingOrder = parsedOrder;
    }

    const newRecipient = {
      email: newRecipientEmail,
      name: newRecipientEmail.split('@')[0], // Use part before @ as name
      signingOrder: signingOrder
    };

    setRecipients([...recipients, newRecipient]);
    setNewRecipientEmail('');
    setNewRecipientSigningOrder('');
    setEmailError('');
    setSigningOrderError('');
  };

  // Handler for removing a recipient
  const handleRemoveRecipient = (index) => {
    const updatedRecipients = [...recipients];
    updatedRecipients.splice(index, 1);

    // No longer reordering signing orders - keep original signing orders
    setRecipients(updatedRecipients);

    // Update signature fields associated with this recipient
    const updatedFields = signatureFields.filter(field =>
      field.recipientIndex !== index
    ).map(field => {
      // Adjust recipientIndex if it was higher than the removed index
      if (field.recipientIndex > index) {
        return {
          ...field,
          recipientIndex: field.recipientIndex - 1
        };
      }
      return field;
    });

    setSignatureFields(updatedFields);
  };

  // Handler for adding a CC recipient
  const handleAddCcRecipient = () => {
    if (!newCcRecipientEmail.trim()) {
      setCcEmailError('Email is required');
      return;
    }

    if (!validateEmail(newCcRecipientEmail)) {
      setCcEmailError('Invalid email format');
      return;
    }

    // Check if email is already added in recipients or CC recipients
    if (recipients.some(r => r.email.toLowerCase() === newCcRecipientEmail.toLowerCase())) {
      setCcEmailError('This email is already added as a recipient');
      return;
    }

    if (ccRecipients.some(r => r.email.toLowerCase() === newCcRecipientEmail.toLowerCase())) {
      setCcEmailError('This email is already added as CC');
      return;
    }

    const newCcRecipient = {
      email: newCcRecipientEmail,
      name: newCcRecipientEmail.split('@')[0] // Use part before @ as name
    };

    setCcRecipients([...ccRecipients, newCcRecipient]);
    setNewCcRecipientEmail('');
    setCcEmailError('');
  };

  // Handler for removing a CC recipient
  const handleRemoveCcRecipient = (index) => {
    const updatedCcRecipients = [...ccRecipients];
    updatedCcRecipients.splice(index, 1);
    setCcRecipients(updatedCcRecipients);
  };

  // Handler for selecting a field type
  const handleFieldTypeSelect = (type) => {
    setSelectedFieldType(type);
    setIsPlacingField(true);
  };

  // Handler for placing a field on the PDF
  const handlePlaceField = (e, pageNumber) => {
    if (!isPlacingField) return;

    const pageRect = e.currentTarget.getBoundingClientRect();
    const pageX = e.clientX - pageRect.left;
    const pageY = e.clientY - pageRect.top;


    // // Calculate position as percentage of page size
    const xPercentage = (pageX / pageRect.width) * 100;
    const yPercentage = (pageY / pageRect.height) * 100;

    // Default field dimensions (as percentage of page)
    let width, height;

    switch (selectedFieldType) {
      case 'signature':
        width = 20;
        height = 10;
        break;
      case 'name':
      case 'date':
        width = 15;
        height = 5;
        break;
      case 'signatureBlock':
        width = 25;
        height = 15;
        break;
      case 'stamp':
        width = 10;
        height = 10;
        break;
      default:
        width = 20;
        height = 10;
    }

    // Add the new field
    const newField = {
      id: `temp-${Date.now()}`,
      fieldType: selectedFieldType,
      pageNumber,
      xPosition: xPercentage,
      yPosition: yPercentage,
      width,
      height,
      isRequired: true,
      recipientIndex: null,
      recipientId: null
    };

    // If there's only one recipient, auto-assign the field
    if (recipients.length === 1) {
      newField.recipientIndex = 0;
      newField.recipientId = recipients[0].id;
      setSignatureFields([...signatureFields, newField]);
    } else {
      setSignatureFields([...signatureFields, newField]);

      // Show recipient selector
      setSelectedFieldIndex(signatureFields.length);
      setShowRecipientSelector(true);
      setRecipientSelectorPosition({
        top: e.clientY,
        left: e.clientX
      });
    }

    setIsPlacingField(false);
  };

  // Handler for field right-click to assign recipient
  const handleFieldRightClick = (e, index) => {
    e.preventDefault();
    setSelectedFieldIndex(index);
    setShowRecipientSelector(true);
    setRecipientSelectorPosition({
      top: e.clientY,
      left: e.clientX
    });
  };

  // Handler for assigning a recipient to a field
  const handleAssignRecipient = (recipientIndex) => {
    const updatedFields = [...signatureFields];
    updatedFields[selectedFieldIndex] = {
      ...updatedFields[selectedFieldIndex],
      recipientIndex,
      recipientId: recipients[recipientIndex].id
    };

    setSignatureFields(updatedFields);
    setShowRecipientSelector(false);
  };

  // Handler for removing a field
  const handleRemoveField = (index) => {
    const updatedFields = [...signatureFields];
    updatedFields.splice(index, 1);
    setSignatureFields(updatedFields);
  };

  // Handler for document title change
  const handleDocumentTitleChange = (event) => {
    setDocumentTitle(event.target.value);
  };

  // Handler for canceling the flow
  const handleCancel = () => {
    history.push('/e-sign');
  };

  // Handler for navigating to the next step
  const handleNext = async () => {
    if (loading) return; // Prevent multiple submissions

    if (activeStep === 0) {
      // Upload/Select document step
      if (!file && !documentId) {
        notify({
          type: 'error',
          message: selectionMode === 'upload' ? 'Please select a file to upload' : 'Please select a document from the suite',
        });
        return;
      }

      if (!documentId && file) {
        try {
          setLoading(true);
          // Upload the document (whether it was uploaded directly or fetched from suite)
          const formData = new FormData();
          formData.append('file', file);

          const response = await ESignService.uploadDocument(formData);
          if (response && response.data && response.data.documentId) {
            setDocumentId(response.data.documentId);
            setActiveStep(1);
          } else {
            throw new Error('Failed to upload document');
          }

        } catch (error) {
          console.error('Error uploading document:', error);
          notify({
            type: 'error',
            message: 'An error occurred while uploading the document',
          });
          setLoading(false);
        }
        finally {
          setLoading(false);
        }
      } else if (documentId) {
        // Already have a document ID (editing case)
        setActiveStep(1);
      }
    } else if (activeStep === 1) {
      // Add recipients step
      if (recipients.length === 0) {
        notify({
          type: 'error',
          message: 'Please add at least one recipient',
        });
        return;
      }

      try {
        setLoading(true);
        // // In a real implementation, send recipients to the server
        // // For now, simulate an API call with a timeout
        // setTimeout(() => {
        //   setActiveStep(2);
        //   setLoading(false);
        // }, 1500);

        // When ready to use the real API:
        const response = await ESignService.addRecipients(documentId, { 
          recipients,
          ccRecipients 
        });
        if (response && response.data && response.data.success) {
          // Update recipient IDs if necessary
          if (response.data.recipients) {
            const updatedRecipients = response.data.recipients.map((r) => ({
              ...r,
              id: r.id
            }));
            setRecipients(updatedRecipients);
          }
          // Update CC recipient IDs if necessary
          if (response.data.ccRecipients) {
            const updatedCcRecipients = response.data.ccRecipients.map((r) => ({
              ...r,
              id: r.id
            }));
            setCcRecipients(updatedCcRecipients);
          }
          setLoading(false);
          setActiveStep(2);
        } else {
          throw new Error('Failed to add recipients');
        }

      } catch (error) {
        console.error('Error adding recipients:', error);
        notify({
          type: 'error',
          message: 'An error occurred while adding recipients',
        });
        setLoading(false);
      }
    }
  };

  // Handler for sending the document
  const handleSend = async () => {
    if (loading) return; // Prevent multiple submissions

    if (signatureFields.length === 0) {
      notify({
        type: 'error',
        message: 'Please add at least one signature field',
      });
      return;
    }

    if (!allFieldsAssigned) {
      notify({
        type: 'error',
        message: 'Please assign all signature fields to recipients',
      });
      return;
    }

    try {
      setLoading(true);

      // // In a real implementation, first add signature fields, then send the document
      // // For now, simulate API calls with timeouts

      // // Simulate adding signature fields
      // setTimeout(async () => {
      //   // Simulate sending the document
      //   setTimeout(() => {
      //     notify({
      //       type: 'success',
      //       message: 'Document has been sent for signature',
      //     });
      //     history.push('/e-sign');
      //   }, 1000);
      // }, 1000);

      // When ready to use the real API:
      // First, add signature fields
      const fieldData = {
        fields: signatureFields.map(field => ({
          recipientId: field.recipientId,
          fieldType: field.fieldType,
          pageNumber: field.pageNumber,
          xPosition: field.xPosition,
          yPosition: field.yPosition,
          width: field.width,
          height: field.height,
          isRequired: field.isRequired,
        })),
        originalHeightInPx: pdfDimensions.height,
        originalWidthInPx: pdfDimensions.width
      };

      const fieldsResponse = await ESignService.addSignatureFields(documentId, fieldData);

      if (!fieldsResponse || !fieldsResponse.data || !fieldsResponse.data.success) {
        throw new Error('Failed to add signature fields');
      }

      // Then, send the document
      const sendResponse = await ESignService.sendDocument(documentId, {
        message: message.trim() || 'Please sign this document'
      });

      if (sendResponse && sendResponse.data && sendResponse.data.success) {
        notify({
          type: 'success',
          message: 'Document has been sent for signature',
        });
        history.push('/e-sign');
      } else {
        throw new Error('Failed to send document');
      }

    } catch (error) {
      notify({
        type: 'error',
        message: error.message || 'An error occurred while sending the document',
      });
      setLoading(false);
    }
  };

  // Render upload step content
  const renderUploadStep = () => (
    <div>
      <Typography variant="h6" gutterBottom>
        Add Document
      </Typography>

      {/* Selection Mode Toggle */}
      <div className={classes.selectionModeContainer}>
        <ButtonGroup variant="outlined">
          <Button
            className={`${classes.selectionModeButton} ${selectionMode === 'upload' ? 'active' : ''}`}
            onClick={() => handleSelectionModeChange('upload')}
            startIcon={<CloudUploadIcon />}
          >
            Upload File
          </Button>
          <Button
            className={`${classes.selectionModeButton} ${selectionMode === 'select' ? 'active' : ''}`}
            onClick={() => handleSelectionModeChange('select')}
            startIcon={<FolderIcon />}
          >
            Select from Suite
          </Button>
        </ButtonGroup>
      </div>

      {/* File input should always be rendered, just hidden */}
      <input
        accept="application/pdf"
        className={classes.fileInput}
        id="file-upload-input"
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: 'none' }} // hidden but still accessible via ref
      />

      {uploadedFileName ? (
        // Show selected/uploaded file
        <div className={classes.uploadedFile}>
          <PdfIcon className={classes.fileIcon} />
          <div className={classes.fileDetails}>
            <Typography variant="subtitle1">{uploadedFileName}</Typography>
            <Typography variant="body2" color="textSecondary">{fileSize}</Typography>
            {selectionMode === 'select' && (
              <Typography variant="caption" color="textSecondary">
                Selected from Document Suite
              </Typography>
            )}
          </div>
          <Button
            variant="outlined"
            size="small"
            onClick={selectionMode === 'upload' ? handleUploadClick : handleSelectFromSuite}
            className={classes.downloadButton}
          >
            Change
          </Button>
        </div>
      ) : (
        // Show selection area based on mode
        selectionMode === 'upload' ? (
          <div className={classes.uploadArea} onClick={handleUploadClick}>
            <Typography variant="body1" style={{ marginBottom: '8px' }}>
              Drag and drop files here
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
              File not uploaded! Only PDF files of up to 5MB accepted.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CloudUploadIcon />}
            >
              Upload File
            </Button>
          </div>
        ) : (
          <div className={classes.uploadArea} onClick={handleSelectFromSuite}>
            <Typography variant="body1" style={{ marginBottom: '8px' }}>
              Choose from your existing documents
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
              Select a PDF document from your document suite.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FolderIcon />}
            >
              Browse Documents
            </Button>
          </div>
        )
      )}
    </div>
  );


  // Render recipients step content
  const renderRecipientsStep = () => (
    <div>
      <Typography variant="h6" gutterBottom>
        Add Recipients
      </Typography>

      <div className={classes.recipientForm}>
        <Typography variant="subtitle2" gutterBottom>
          To
        </Typography>

        <div style={{ display: 'flex', gap: '8px' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Email"
            value={newRecipientEmail}
            onChange={(e) => {
              setNewRecipientEmail(e.target.value);
              if (emailError) setEmailError('');
              if (signingOrderError) setSigningOrderError('');
            }}
            error={!!emailError}
            helperText={emailError}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddRecipient();
              }
            }}
            style={{ flex: 2 }}
          />
          <TextField
            variant="outlined"
            size="small"
            placeholder="Signing Order (optional)"
            value={newRecipientSigningOrder}
            onChange={(e) => {
              setNewRecipientSigningOrder(e.target.value);
              if (signingOrderError) setSigningOrderError('');
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddRecipient();
              }
            }}
            type="number"
            inputProps={{ min: 1, max: recipients.length + 1 }}
            error={!!signingOrderError}
            helperText={signingOrderError || `Max: ${recipients.length + 1}`}
            style={{ flex: 1 }}
          />
          <Button
            variant="contained"
            color="primary"
            style={{ whiteSpace: 'nowrap', height: 'fit-content' }}
            onClick={handleAddRecipient}
          >
            Add Recipient
          </Button>
        </div>

        <div className={classes.recipientList}>
          {recipients.map((recipient, index) => (
            <Chip
              key={index}
              icon={<PersonIcon />}
              label={`${recipient.name} (${recipient.email}) - Order: ${recipient.signingOrder}`}
              onDelete={() => handleRemoveRecipient(index)}
              className={classes.recipientChip}
              style={{ backgroundColor: recipientColors[index % recipientColors.length] + '20' }}
            />
          ))}
        </div>
        <Box mt={2}>
          <Button
            variant="text"
            color="primary"
            onClick={() => setShowCC(!showCC)}
          >
            {showCC ? 'Hide CC' : 'Show CC'}
          </Button>
        </Box>

        {showCC && (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              CC
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Email"
                fullWidth
                value={newCcRecipientEmail}
                onChange={(e) => {
                  setNewCcRecipientEmail(e.target.value);
                  if (ccEmailError) setCcEmailError('');
                }}
                error={!!ccEmailError}
                helperText={ccEmailError}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddCcRecipient();
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: '8px', whiteSpace: 'nowrap' }}
                onClick={handleAddCcRecipient}
              >
                Add CC
              </Button>
            </div>

            <div className={classes.recipientList}>
              {ccRecipients.map((ccRecipient, index) => (
                <Chip
                  key={index}
                  icon={<PersonIcon />}
                  label={`${ccRecipient.name} (${ccRecipient.email})`}
                  onDelete={() => handleRemoveCcRecipient(index)}
                  className={classes.recipientChip}
                  style={{ backgroundColor: '#FFF3E0' }}
                />
              ))}
            </div>
          </Box>
        )}
      </div>

      <div className={classes.messageField}>
        <Typography variant="subtitle2" gutterBottom>
          Add Optional Comment
        </Typography>
        <TextField
          variant="outlined"
          multiline
          rows={4}
          fullWidth
          placeholder="Add a comment for your recipients"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>
    </div>
  );

  const renderSignatureStep = () => (
    <div>
      <Typography variant="h6" gutterBottom>
        Add Signature
      </Typography>

      <div className={classes.signatureFieldButtons}>
        {fieldTypes.map((field) => (
          <Button
            key={field.type}
            variant={selectedFieldType === field.type ? "contained" : "outlined"}
            className={classes.fieldButton}
            onClick={() => handleFieldTypeSelect(field.type)}
            style={{
              borderColor: field.color,
              backgroundColor: selectedFieldType === field.type ? field.color + '20' : 'white',
              color: field.color
            }}
          >
            {field.label}
          </Button>
        ))}
      </div>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Drag and drop the fields on the desired location
      </Typography>

      {/* Page navigation for multi-page PDFs */}
      {pdfNumPages && pdfNumPages > 1 && (
        <div style={{ marginBottom: '10px', textAlign: 'center' }}>
          <Button
            onClick={() => setPdfPageNumber(Math.max(1, pdfPageNumber - 1))}
            disabled={pdfPageNumber <= 1}
            size="small"
          >
            Previous
          </Button>
          <Typography variant="body2" style={{ margin: '0 15px', display: 'inline' }}>
            Page {pdfPageNumber} of {pdfNumPages}
          </Typography>
          <Button
            onClick={() => setPdfPageNumber(Math.min(pdfNumPages, pdfPageNumber + 1))}
            disabled={pdfPageNumber >= pdfNumPages}
            size="small"
          >
            Next
          </Button>
        </div>
      )}

      <div className={classes.pdfViewer}>
        <div
          className={classes.pdfPage}
          style={{
            width: pdfDimensions.width * scale,
            height: pdfDimensions.height * scale,
            position: 'relative'
          }}
          onClick={(e) => handlePlaceField(e, pdfPageNumber)}
        >
          {/* Render actual PDF or fallback content */}
          {uploadedFile ? (
            <Document
              file={uploadedFile}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '400px'
                }}>
                  <Typography>Loading PDF...</Typography>
                </div>
              }
            >
              <Page
                pageNumber={pdfPageNumber}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                onLoadSuccess={(page) => {
                  // Update PDF dimensions based on actual page
                  setPdfDimensions({
                    width: page.width,
                    height: page.height
                  });
                }}
              />
            </Document>
          ) : pdfError ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
              flexDirection: 'column'
            }}>
              <Typography color="error" gutterBottom>
                {pdfError}
              </Typography>
              <Button onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </div>
          ) : (
            // Fallback content when no file is uploaded (your original hardcoded content)
            <div style={{ padding: '20px' }}>
              <Typography variant="h4" align="center" style={{ marginBottom: '40px' }}>
                INVOICE
              </Typography>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
                <div>
                  <Typography variant="subtitle1">ISSUED TO:</Typography>
                  <Typography>Richard Sanchez</Typography>
                  <Typography>Thynk Unlimited</Typography>
                  <Typography>123 Anywhere St., Any City</Typography>
                </div>
                <div>
                  <Typography><strong>INVOICE NO:</strong> 01234</Typography>
                  <Typography><strong>DATE:</strong> 11.02.2030</Typography>
                  <Typography><strong>DUE DATE:</strong> 11.03.2030</Typography>
                </div>
              </div>

              <div style={{ marginBottom: '30px' }}>
                <Typography variant="subtitle1">PAY TO:</Typography>
                <Typography>Borcele Bank</Typography>
                <Typography>Account Name: Adeline Palmerston</Typography>
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                    <th style={{ textAlign: 'left', padding: '8px' }}>DESCRIPTION</th>
                    <th style={{ textAlign: 'right', padding: '8px' }}>UNIT PRICE</th>
                    <th style={{ textAlign: 'right', padding: '8px' }}>QTY</th>
                    <th style={{ textAlign: 'right', padding: '8px' }}>TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ textAlign: 'left', padding: '8px' }}>Brand consultation</td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>100</td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>1</td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>$100</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ textAlign: 'left', padding: '8px' }}>Logo design</td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>100</td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>1</td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>$100</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ textAlign: 'left', padding: '8px' }}>Website design</td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>100</td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>1</td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>$100</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ textAlign: 'left', padding: '8px' }}>Social media templates</td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>100</td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>1</td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>$100</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'right', padding: '8px' }}><strong>SUBTOTAL</strong></td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>$400</td>
                  </tr>
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'right', padding: '8px' }}>Tax (10%)</td>
                    <td style={{ textAlign: 'right', padding: '8px' }}>$40</td>
                  </tr>
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'right', padding: '8px' }}><strong>TOTAL</strong></td>
                    <td style={{ textAlign: 'right', padding: '8px' }}><strong>$440</strong></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Render signature fields - these will overlay on top of the PDF */}
          {signatureFields
            .filter(field => field.pageNumber === pdfPageNumber)
            .map((field) => {
              const fieldType = fieldTypes.find(f => f.type === field.fieldType) || fieldTypes[0];
              const recipientIndex = field.recipientIndex;
              const recipientColor = recipientIndex !== null
                ? recipientColors[recipientIndex % recipientColors.length]
                : '#CCCCCC';

              return (
                <Rnd
                  key={field.id}
                  size={{
                    width: `${field.width}%`,
                    height: `${field.height}%`
                  }}
                  position={{
                    x: (field.xPosition / 100) * (pdfDimensions.width * scale),
                    y: (field.yPosition / 100) * (pdfDimensions.height * scale)
                  }}
                  bounds="parent"
                  enableResizing={true}
                  onDragStop={(e, d) => {
                    const xPct = (d.x / (pdfDimensions.width * scale)) * 100;
                    const yPct = (d.y / (pdfDimensions.height * scale)) * 100;
                    const updatedFields = signatureFields.map(f =>
                      f.id === field.id
                        ? { ...f, xPosition: xPct, yPosition: yPct }
                        : f
                    );
                    setSignatureFields(updatedFields);
                  }}
                  onResizeStop={(e, direction, ref, delta, position) => {
                    const widthPct = (ref.offsetWidth / (pdfDimensions.width * scale)) * 100;
                    const heightPct = (ref.offsetHeight / (pdfDimensions.height * scale)) * 100;
                    const xPct = (position.x / (pdfDimensions.width * scale)) * 100;
                    const yPct = (position.y / (pdfDimensions.height * scale)) * 100;
                    const updatedFields = signatureFields.map(f =>
                      f.id === field.id
                        ? {
                          ...f,
                          width: widthPct,
                          height: heightPct,
                          xPosition: xPct,
                          yPosition: yPct
                        }
                        : f
                    );
                    setSignatureFields(updatedFields);
                  }}
                  className={classes.fieldMarker}
                  style={{
                    backgroundColor: `${recipientColor}20`,
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    position: 'absolute'
                  }}
                  onContextMenu={(e) => handleFieldRightClick(e, signatureFields.findIndex(f => f.id === field.id))}
                >
                  {fieldType.label}
                  {recipientIndex !== null && (
                    <div
                      className={classes.selectedRecipient}
                      style={{ backgroundColor: recipientColor }}
                    >
                      {recipientIndex + 1}
                    </div>
                  )}
                  <IconButton
                    size="small"
                    style={{
                      position: 'absolute',
                      top: '-12px',
                      right: '-12px',
                      backgroundColor: 'white',
                      padding: '2px',
                      zIndex: 11
                    }}
                    onClick={() => handleRemoveField(signatureFields.findIndex(f => f.id === field.id))}
                  >
                    <DeleteIcon style={{ fontSize: '16px' }} />
                  </IconButton>
                </Rnd>
              );
            })}
        </div>
      </div>

      {/* Recipient selector popup */}
      {showRecipientSelector && (
        <div
          className={classes.recipientSelector}
          style={{
            top: recipientSelectorPosition.top,
            left: recipientSelectorPosition.left,
            zIndex: 20
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Assign to recipient:
          </Typography>
          <RadioGroup>
            {recipients.map((recipient, index) => (
              <FormControlLabel
                key={index}
                value={index.toString()}
                control={<Radio style={{ color: recipientColors[index % recipientColors.length] }} />}
                label={recipient.name}
                onClick={() => handleAssignRecipient(index)}
              />
            ))}
          </RadioGroup>
        </div>
      )}
    </div>
  );

  // Get content based on active step
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderUploadStep();
      case 1:
        return renderRecipientsStep();
      case 2:
        return renderSignatureStep();
      default:
        return 'Unknown step';
    }
  };

  return (
    <div className={classes.root}>
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          zIndex: 9999,
        }}>
          <CircularProgress />
        </div>
      )}

      <Paper className={classes.paper}>
        <div className={classes.header}>
          <Typography variant="h5" className={classes.title}>
            GET SIGNATURES
          </Typography>
          <div>
            <Button
              className={classes.buttonCancel}
              onClick={handleCancel}
              style={{ marginRight: '8px' }}
            >
              Cancel
            </Button>

            {activeStep === 2 ? (
              <Button
                variant="contained"
                className={allFieldsAssigned && !loading ? classes.buttonSend : classes.buttonDisabled}
                disabled={!allFieldsAssigned || loading}
                onClick={handleSend}
              >
                {loading ? 'Sending...' : 'Send'}
              </Button>
            ) : (
              <Button
                variant="contained"
                className={classes.buttonNext}
                onClick={handleNext}
              >
                Next
              </Button>
            )}
          </div>
        </div>

        <div className={classes.contentArea}>
          <div className={classes.stepperContainer}>
            <VerticalStepperComponent steps={steps} activeStep={activeStep} />
          </div>

          <div className={classes.contentContainer}>
            {getStepContent(activeStep)}
          </div>
        </div>
      </Paper>

      {/* Document Selection Modal */}
      <DocumentSelectionModal
        open={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
        onSelect={handleDocumentSelection}
        currentCompany={currentCompany}
      />
    </div>
  );
}